import { createFileRoute, Link } from "@tanstack/react-router";
import { 
  Music2, Sparkles, Brain, Radio, Building2, Coffee, Hotel, ShoppingBag, 
  Play, Pause, Check, Clock, Smile, Award, Timer, Activity, Sliders, Mic, 
  RefreshCw, Utensils, Wine, Briefcase, ChevronRight, Zap, TrendingUp, 
  Volume2, Shield, Layers, ArrowRight, Globe, AlertCircle, Search 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlayer } from "@/stores/player";
import { PlayerBar } from "@/components/PlayerBar";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Sura — Neuroscience backed ambient music to drive better customer experience" },
      { name: "description", content: "Sura combines consumer behaviour science, neuroscience research, original music IP, and AI-assisted composition to create soundscapes engineered for real-world business outcomes." },
      { property: "og:title", content: "Sura — Neuroscience backed ambient music to drive better customer experience" },
      { property: "og:description", content: "Neuroscience-backed ambient music for restaurants, cafés, hotels, retailers, and workspaces." },
    ],
  }),
  component: Landing,
});

type Track = { 
  id: string; 
  title: string; 
  duration?: number; 
  is_instrumental?: boolean; 
  album?: { id: string; title: string }; 
  album_title?: string; 
  bpm?: number; 
  key?: string 
};

// Demo tracks for landing page
const DEMO_TRACKS: Track[] = [
  { id: "demo-track-1", title: "Fogged Rearview", album_title: "Morning Drift", duration: 180, is_instrumental: true },
  { id: "demo-track-2", title: "Morning Window Drift", album_title: "Morning Drift", duration: 210, is_instrumental: true },
  { id: "demo-track-3", title: "Gravel Rain", album_title: "Road Trip", duration: 195, is_instrumental: true },
  { id: "demo-track-4", title: "Seoul Spice, Bangalore Heat", album_title: "Urban Vibes", duration: 165, is_instrumental: true },
];

function BackgroundWaves() {
  return (
    <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none opacity-[0.18]">
      <svg className="absolute w-[200%] h-full left-[-50%] top-0 animate-pulse-slow" viewBox="0 0 1000 800" preserveAspectRatio="none">
        <path
          d="M0,350 Q250,150 500,350 T1000,350"
          fill="none"
          stroke="url(#sw-grad-1)"
          strokeWidth="3.5"
          className="animate-wave-slow opacity-60"
        />
        <path
          d="M0,420 Q250,220 500,420 T1000,420"
          fill="none"
          stroke="url(#sw-grad-2)"
          strokeWidth="2.5"
          className="animate-wave-medium opacity-40"
        />
        <path
          d="M0,480 Q250,300 500,480 T1000,480"
          fill="none"
          stroke="url(#sw-grad-3)"
          strokeWidth="1.5"
          className="animate-wave-fast opacity-25"
        />
        <defs>
          <linearGradient id="sw-grad-1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="oklch(0.7 0.16 240 / 0)" />
            <stop offset="30%" stopColor="oklch(0.7 0.16 240 / 0.5)" />
            <stop offset="50%" stopColor="oklch(0.58 0.18 250 / 0.8)" />
            <stop offset="70%" stopColor="oklch(0.65 0.16 220 / 0.5)" />
            <stop offset="100%" stopColor="oklch(0.65 0.16 220 / 0)" />
          </linearGradient>
          <linearGradient id="sw-grad-2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="oklch(0.65 0.16 220 / 0)" />
            <stop offset="30%" stopColor="oklch(0.58 0.18 250 / 0.4)" />
            <stop offset="50%" stopColor="oklch(0.7 0.16 240 / 0.6)" />
            <stop offset="70%" stopColor="oklch(0.58 0.18 250 / 0.4)" />
            <stop offset="100%" stopColor="oklch(0.58 0.18 250 / 0)" />
          </linearGradient>
          <linearGradient id="sw-grad-3" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="oklch(0.7 0.16 240 / 0)" />
            <stop offset="50%" stopColor="oklch(0.65 0.16 220 / 0.3)" />
            <stop offset="100%" stopColor="oklch(0.65 0.16 220 / 0)" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

function AmbientGlow({ className = "" }: { className?: string }) {
  return (
    <div className={`absolute -z-20 w-[500px] h-[500px] rounded-full bg-primary/15 blur-[130px] pointer-events-none ${className}`} />
  );
}

function Landing() {
  const current = usePlayer((s) => s.current);
  const isPlaying = usePlayer((s) => s.isPlaying);
  const playTrack = usePlayer((s) => s.playTrack);
  const setPlaying = usePlayer((s) => s.setPlaying);

  const handleTrackPlay = (track: Track) => {
    if (current?.id === track.id) {
      setPlaying(!isPlaying);
    } else {
      playTrack(track, DEMO_TRACKS);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden select-none">

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg">
              <Music2 className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-semibold tracking-tight">Sura</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <a href="#why-exists" className="hover:text-foreground transition-colors">Why Sura</a>
            <a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a>
            <a href="#why-choose" className="hover:text-foreground transition-colors">Why Choose Us</a>
            <a href="#spaces" className="hover:text-foreground transition-colors">Spaces</a>
          </nav>
          <nav className="flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Sign In</Link>
            <Link
              to="/signup"
              className="px-5 py-2.5 rounded-full text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 hover:shadow-primary/30 hover:scale-105 transition-all"
            >
              Get Started
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-24 md:pt-32 md:pb-36">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "var(--gradient-hero)" }}
        />
        <BackgroundWaves />
        <AmbientGlow className="top-10 left-10 opacity-30" />
        
        <div className="mx-auto max-w-7xl px-6 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 text-sm font-medium text-foreground mb-8 animate-fade-in-up">
            <Brain className="h-4 w-4 text-primary animate-pulse" />
            <span className="font-bold">Neuroscience-Backed Ambient Music for Business</span>
          </div>
          
          {/* Title */}
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight max-w-6xl mx-auto leading-[1.15] mb-6 animate-fade-in-up delay-100 text-zinc-900">
            Neuroscience backed ambient music <br className="hidden md:block" />
            to drive better customer experience
          </h1>
          
          {/* Benefits Bullet points */}
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm md:text-lg font-medium text-muted-foreground/90 mb-8 max-w-3xl mx-auto animate-fade-in-up delay-150">
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" /> Increase dwell time
            </span>
            <span className="hidden md:inline text-muted-foreground/40">•</span>
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" /> Improve customer experience
            </span>
            <span className="hidden md:inline text-muted-foreground/40">•</span>
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4 text-primary shrink-0" /> Build a distinctive brand atmosphere
            </span>
          </div>

          {/* Description */}
          <p className="text-lg md:text-xl text-muted-foreground/85 max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200">
            Sura combines consumer behaviour science, neuroscience research, original music IP, and AI-assisted composition to create soundscapes engineered for real-world business outcomes.
          </p>
          
          {/* Buttons */}
          <div className="flex flex-wrap justify-center gap-4 animate-fade-in-up delay-300">
            <Link
              to="/signup"
              className="px-8 py-4 rounded-full text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-xl shadow-primary/25 hover:shadow-primary/35 hover:scale-105 transition-all flex items-center gap-2"
            >
              <Play className="h-5 w-5 fill-current" />
              Start Free Trial
            </Link>
            <a 
              href="#samples" 
              className="px-8 py-4 rounded-full text-base font-semibold border-2 border-white/20 bg-white/5 backdrop-blur hover:bg-white/10 transition-all flex items-center gap-2"
            >
              <Volume2 className="h-5 w-5" />
              Listen to Samples
            </a>
          </div>
          
          {/* Bottom Trust Line */}
          <p className="mt-8 text-xs md:text-sm text-muted-foreground/60 animate-fade-in-up delay-400 flex flex-wrap justify-center gap-x-4 gap-y-1">
            <span>No credit card required</span>
            <span>•</span>
            <span>Commercial rights included</span>
            <span>•</span>
            <span>14-Day Free Trial</span>
          </p>

          <p className="mt-12 text-sm font-semibold tracking-wider text-muted-foreground/50 uppercase">
            Trusted by Restaurants, Cafés, Hotels, Retailers & Workspaces
          </p>
        </div>

        {/* Current Session / Audio Visualization */}
        <div id="samples" className="mx-auto max-w-5xl px-6 mt-16 animate-fade-in-up delay-500">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-purple-600/60 flex items-center justify-center shadow-lg">
                  <Music2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold text-lg flex items-center gap-2">
                    Current Session 
                    {isPlaying && <span className="h-2 w-2 rounded-full bg-green-500 animate-ping" />}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {current ? `${current.album_title || 'Sura Original'} • ${current.title}` : "Select a sample below to experience Sura"}
                  </div>
                </div>
              </div>
              <Button 
                size="lg" 
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 hover:scale-105 transition-all w-full md:w-auto"
                onClick={() => handleTrackPlay(DEMO_TRACKS[0])}
              >
                {isPlaying && current?.id === DEMO_TRACKS[0].id ? (
                  <>
                    <Pause className="h-5 w-5 mr-2 fill-current" />
                    Pause Session
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5 mr-2 fill-current animate-pulse" />
                    Listen Now
                  </>
                )}
              </Button>
            </div>

            {/* Visualizer bars */}
            <div className="h-24 md:h-32 flex items-center justify-center gap-1.5 overflow-hidden bg-black/10 rounded-2xl p-4 border border-white/5">
              {[...Array(48)].map((_, i) => {
                const heightVal = isPlaying 
                  ? `${30 + Math.sin(i * 0.8 + Date.now() / 150) * 45}%` 
                  : "8%";
                return (
                  <div
                    key={i}
                    className={`w-1.5 rounded-t-lg transition-all duration-300 ${
                      isPlaying 
                        ? 'bg-gradient-to-t from-primary via-purple-500 to-accent animate-music-bar' 
                        : 'bg-muted/30'
                    }`}
                    style={{
                      height: heightVal,
                      animationDelay: isPlaying ? `${i * 0.04}s` : '0s',
                      animationDuration: isPlaying ? `${0.8 + Math.random() * 0.8}s` : '0s',
                    }}
                  />
                );
              })}
            </div>

            {/* Demo Track list grid */}
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-bold">Try Our Samples</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {DEMO_TRACKS.map((track) => {
                  const isCurrentPlaying = current?.id === track.id && isPlaying;
                  return (
                    <button
                      key={track.id}
                      onClick={() => handleTrackPlay(track)}
                      className={`group flex items-center gap-3 p-4 rounded-2xl border transition-all text-left shadow-lg ${
                        current?.id === track.id 
                          ? 'bg-primary/10 border-primary/40' 
                          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-primary/30'
                      }`}
                    >
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shrink-0 ${
                        isCurrentPlaying ? 'bg-primary text-primary-foreground' : 'bg-primary/20 text-primary'
                      }`}>
                        {isCurrentPlaying ? (
                          <Pause className="h-4 w-4 fill-current" />
                        ) : (
                          <Play className="h-4 w-4 fill-current" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate text-foreground group-hover:text-primary transition-colors flex items-center gap-1.5">
                          {track.title}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{track.album_title}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Why Sura Exists */}
      <section id="why-exists" className="py-24 md:py-32 relative bg-background border-t border-border/40 overflow-hidden">
        <BackgroundWaves />
        <AmbientGlow className="bottom-10 right-10 opacity-45" />
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-xs font-semibold text-primary">
                <AlertCircle className="h-3.5 w-3.5 animate-pulse" />
                <span>The Ambient Music Problem</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Why Sura Exists
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                Most businesses spend heavily on interiors, lighting, branding and service.
              </p>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                Yet one of the most powerful influences on customer behaviour is often left to random playlists, YouTube streams or staff preferences.
              </p>
              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 shadow-lg">
                <p className="text-foreground font-semibold text-md italic">
                  "Sura transforms music from entertainment into behavioural infrastructure."
                </p>
              </div>
            </div>

            {/* Right Grid (Influences List) */}
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {[
                { title: "Dwell Time", desc: "Influences how long customers stay in physical environments.", icon: Clock, color: "text-blue-400" },
                { title: "Shopping Behaviour", desc: "Affects item choices, spending rates, and purchase volume.", icon: TrendingUp, color: "text-green-400" },
                { title: "Emotional Comfort", desc: "Reduces anxiety, fosters warm feelings, and creates relaxation.", icon: Smile, color: "text-yellow-400" },
                { title: "Perceived Waiting Time", desc: "Makes queue and wait times feel up to 30% shorter.", icon: Timer, color: "text-red-400" },
                { title: "Brand Perception", desc: "Expresses quality, character, and consistency in seconds.", icon: Award, color: "text-purple-400" },
                { title: "Employee Mood", desc: "Boosts team focus, reduces fatigue, and increases productivity.", icon: Sparkles, color: "text-pink-400" },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="group p-6 rounded-3xl bg-card border border-border shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-soft)] hover:border-primary/30 transition-all duration-300">
                    <div className="h-12 w-12 rounded-xl bg-white/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <Icon className={`h-6 w-6 ${item.color}`} />
                    </div>
                    <h3 className="text-lg font-bold mb-2 flex items-center gap-1.5">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </section>

      {/* How Sura Works */}
      <section id="how-it-works" className="py-24 md:py-32 bg-secondary/20 border-t border-b border-border/40 relative overflow-hidden">
        <BackgroundWaves />
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-xs font-semibold text-primary">
              <Activity className="h-3.5 w-3.5" />
              <span>Scientific Methodology</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">How Sura Works</h2>
            
            {/* Science -> Music -> Behaviour -> Results flow */}
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 pt-4 text-sm md:text-lg font-semibold text-primary">
              <span>Science</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span>Music</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span>Behaviour</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span>Results</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Consumer Behaviour Research",
                desc: "Retail experts, hospitality specialists and behavioural scientists identify the conditions that influence customer experience and spending behaviour.",
                icon: Search,
                extra: (
                  <div className="mt-6 pt-6 border-t border-border/60 space-y-3">
                    <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">Focus Variables</span>
                    <div className="flex flex-wrap gap-1.5">
                      {["Dwell Time", "Spending Patterns", "Customer Flow", "Friction Points"].map((tag) => (
                        <span key={tag} className="text-[11px] px-2.5 py-1.5 bg-secondary border border-border/50 rounded-lg text-foreground font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              },
              {
                step: "2",
                title: "Neuroscience Frameworks",
                desc: "Research is translated into sound blueprints defining optimal attributes for your business.",
                icon: Brain,
                extra: (
                  <div className="mt-6 pt-6 border-t border-border/60 space-y-3">
                    <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">Sound Blueprints</span>
                    <div className="grid grid-cols-2 gap-2">
                      {["Tempo", "Rhythm", "Harmonic Structure", "Frequency Balance", "Energy Curves", "Time Perception"].map((bp) => (
                        <span key={bp} className="inline-flex items-center gap-1.5 text-[11px] text-foreground bg-secondary px-2.5 py-1.5 rounded-lg border border-border/50">
                          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                          {bp}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              },
              {
                step: "3",
                title: "Sura Music Creator™",
                desc: "Our proprietary AI-assisted music creation platform converts these blueprints into original compositions.",
                icon: Sliders,
                extra: (
                  <div className="mt-6 pt-6 border-t border-border/60 space-y-4">
                    <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">Synthesis Parameters</span>
                    <div className="space-y-3">
                      {[
                        { name: "Tempo Alignment", val: 75 },
                        { name: "Frequency Balance", val: 85 },
                        { name: "Harmonic Warmth", val: 65 },
                      ].map((slider) => (
                        <div key={slider.name} className="space-y-1">
                          <div className="flex justify-between text-[11px] text-muted-foreground font-medium">
                            <span>{slider.name}</span>
                            <span>{slider.val}%</span>
                          </div>
                          <div className="h-1.5 bg-secondary rounded-full overflow-hidden border border-border/50">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${slider.val}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              },
              {
                step: "4",
                title: "Independent Artists",
                desc: "Musicians, producers and composers collaborate with Sura to transform scientific frameworks into emotionally rich music.",
                icon: Mic,
                extra: (
                  <div className="mt-6 pt-6 border-t border-border/60 space-y-3">
                    <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">Ecosystem Metrics</span>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center bg-secondary p-2.5 rounded-lg border border-border/50 text-xs text-muted-foreground font-medium">
                        <span>Featured Artists</span>
                        <span className="text-foreground font-bold">500+ Creators</span>
                      </div>
                      <div className="flex justify-between items-center bg-secondary p-2.5 rounded-lg border border-border/50 text-xs text-muted-foreground font-medium">
                        <span>Global Clearance</span>
                        <span className="text-foreground font-bold">100% Pre-cleared</span>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                step: "5",
                title: "Real-World Deployment",
                desc: "Businesses stream music through Sura for consistent, commercially licensed playback across locations.",
                icon: Globe,
                extra: (
                  <div className="mt-6 pt-6 border-t border-border/60 space-y-3">
                    <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">Deployment State</span>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs bg-secondary p-2.5 rounded-lg border border-border/50 text-muted-foreground font-medium">
                        <span className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                          Streaming Status
                        </span>
                        <span className="text-green-500 font-bold">ACTIVE</span>
                      </div>
                      <div className="flex items-center justify-between text-xs bg-secondary p-2.5 rounded-lg border border-border/50 text-muted-foreground font-medium">
                        <span>High-Fidelity Audio</span>
                        <span className="text-foreground font-bold">320kbps</span>
                      </div>
                    </div>
                  </div>
                )
              },
              {
                step: "6",
                title: "Continuous Learning",
                desc: "Performance data improves future soundscapes, creating a continuously evolving behaviour engine.",
                icon: RefreshCw,
                extra: (
                  <div className="mt-6 pt-6 border-t border-border/60 space-y-3">
                    <span className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">Optimization Engine</span>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs bg-secondary p-2.5 rounded-lg border border-border/50 text-muted-foreground font-medium">
                        <span>Feedback Data</span>
                        <span className="text-foreground font-bold">Real-time Stream</span>
                      </div>
                      <div className="flex items-center justify-between text-xs bg-secondary p-2.5 rounded-lg border border-border/50 text-muted-foreground font-medium">
                        <span>Adaptation Rate</span>
                        <span className="text-foreground font-bold">Continuous</span>
                      </div>
                    </div>
                  </div>
                )
              },
            ].map((stepItem, idx) => {
              const Icon = stepItem.icon;
              return (
                <div key={idx} className="relative group p-8 rounded-3xl bg-card border border-border shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-soft)] hover:border-primary/20 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <span className="text-4xl font-extrabold text-muted-foreground/20 group-hover:text-primary/25 transition-colors">
                        0{stepItem.step}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{stepItem.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{stepItem.desc}</p>
                  </div>
                  {stepItem.extra}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Businesses Choose Sura */}
      <section id="why-choose" className="py-24 md:py-32 relative bg-background border-b border-border/40 overflow-hidden">
        <BackgroundWaves />
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-xs font-semibold text-primary">
              <Zap className="h-3.5 w-3.5" />
              <span>Built For Outcomes, Not Streams</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">Why Businesses Choose Sura</h2>
            <p className="text-lg text-muted-foreground">
              Unlike consumer music platforms, Sura is designed specifically for physical environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Improve Customer Experience", desc: "Create environments where customers feel comfortable, relaxed and engaged.", icon: Smile },
              { title: "Increase Dwell Time", desc: "Encourage guests and shoppers to stay longer naturally.", icon: Clock },
              { title: "Strengthen Brand Identity", desc: "Develop a unique sonic signature that customers associate with your brand.", icon: Sparkles },
              { title: "Simplify Licensing", desc: "Every track is commercially licensed and business-ready.", icon: Shield },
              { title: "Scale Consistently", desc: "Deploy identical experiences across 10, 100 or 10,000 locations.", icon: Layers },
              { title: "Measure Impact", desc: "Track playlists, locations, performance and business outcomes.", icon: TrendingUp },
            ].map((prop, idx) => {
              const Icon = prop.icon;
              return (
                <div key={idx} className="group p-8 rounded-3xl bg-card border border-border shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-soft)] hover:border-primary/30 transition-all duration-300">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold mb-3">{prop.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{prop.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Original Music. Original IP. */}
      <section className="py-24 md:py-32 relative bg-secondary/15 border-b border-border/40 overflow-hidden">
        <BackgroundWaves />
        <AmbientGlow className="top-10 left-10 opacity-30" />
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-xs font-semibold text-primary">
                <Music2 className="h-3.5 w-3.5" />
                <span>100% Original Catalog</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Original Music.<br />Original IP.
              </h2>
              <div className="space-y-4">
                <p className="text-lg font-semibold text-foreground">
                  Every Sura soundtrack is original.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 bg-background p-3 rounded-xl border border-border">
                    <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                    <span>No Bollywood playlists</span>
                  </div>
                  <div className="flex items-center gap-2 bg-background p-3 rounded-xl border border-border">
                    <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                    <span>No chart hits</span>
                  </div>
                  <div className="flex items-center gap-2 bg-background p-3 rounded-xl border border-border">
                    <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                    <span>No copyright uncertainty</span>
                  </div>
                  <div className="flex items-center gap-2 bg-background p-3 rounded-xl border border-border">
                    <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
                    <span>No competitor using same music</span>
                  </div>
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Each composition is created through Sura Music Creator™ in collaboration with independent artists and producers.
                </p>
              </div>
            </div>

            {/* Right Card */}
            <div className="lg:col-span-6">
              <div className="relative p-8 md:p-10 rounded-3xl bg-card border border-border shadow-xl overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-purple-500/5 opacity-100" />
                <div className="relative z-10 space-y-6">
                  <h3 className="text-xl font-bold text-foreground">The result:</h3>
                  <ul className="space-y-4">
                    {[
                      "Exclusive Sound Identity",
                      "Global Commercial Rights",
                      "Consistent Brand Experience",
                      "Strong Intellectual Property Protection"
                    ].map((result, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-base font-semibold">
                        <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                          <Check className="h-3.5 w-3.5 text-green-500" />
                        </div>
                        <span>{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Empowering Independent Creators */}
      <section className="py-24 md:py-32 relative bg-background border-b border-border/40 overflow-hidden">
        <BackgroundWaves />
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Left Graphics/Card */}
            <div className="lg:col-span-6 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-xs font-semibold text-primary">
                <Mic className="h-3.5 w-3.5 animate-pulse" />
                <span>Artist Ecosystem</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Empowering Independent Creators
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                The future of music is being built by independent artists. Across YouTube, Instagram, TikTok and streaming platforms, creators are building audiences without traditional record labels.
              </p>
              <div className="p-8 rounded-3xl bg-gradient-to-br from-primary/10 to-purple-500/10 border border-primary/20 shadow-lg space-y-4">
                <div className="text-xs font-bold tracking-widest text-primary uppercase">Sura Opportunity</div>
                <div className="text-3xl font-black text-foreground">Create Once. <br />Earn Repeatedly.</div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Artists contribute original music to the Sura platform. As tracks are adopted by businesses, creators earn long-term recurring income.
                </p>
              </div>
            </div>

            {/* Right Information Flow */}
            <div className="lg:col-span-6 lg:order-1 space-y-6">
              <p className="text-base text-muted-foreground font-medium">
                As tracks are adopted by businesses:
              </p>
              <div className="space-y-4">
                {[
                  { title: "Usage is tracked", desc: "Every stream and placement is monitored via secure telemetry.", icon: Activity },
                  { title: "Revenue is shared", desc: "Transparent splits and fair distribution for every play.", icon: RefreshCw },
                  { title: "Performance is measured", desc: "See metrics on venue engagement and customer responses.", icon: TrendingUp },
                  { title: "Creators earn long-term recurring income", desc: "The better a track performs, the greater its reach and rewards.", icon: Award },
                ].map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex gap-4 p-5 rounded-2xl bg-card border border-border hover:border-primary/20 transition-all duration-300">
                      <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground mb-1">{item.title}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Built For Every Physical Space */}
      <section id="spaces" className="py-24 md:py-32 relative bg-secondary/10 border-b border-border/40 overflow-hidden">
        <BackgroundWaves />
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-xs font-semibold text-primary">
              <Building2 className="h-3.5 w-3.5" />
              <span>Tailored Solutions</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">Built For Every Physical Space</h2>
            <p className="text-lg text-muted-foreground">
              Provide your customers with the ultimate environmental companion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Restaurants", desc: "Create memorable dining experiences.", icon: Utensils },
              { name: "Cafés", desc: "Encourage longer stays and repeat visits.", icon: Coffee },
              { name: "Hotels", desc: "Deliver premium guest experiences.", icon: Hotel },
              { name: "Retail Stores", desc: "Increase comfort and browsing time.", icon: ShoppingBag },
              { name: "Pubs & Lounges", desc: "Match energy levels to customer flow.", icon: Wine },
              { name: "Offices", desc: "Support focus, wellbeing and productivity.", icon: Briefcase },
            ].map((space, idx) => {
              const Icon = space.icon;
              return (
                <div key={idx} className="group relative overflow-hidden rounded-3xl bg-card border border-border hover:border-primary/50 hover:shadow-[var(--shadow-soft)] transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="p-8 relative z-10 space-y-4 flex flex-col justify-between h-full">
                    <div className="space-y-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{space.name}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{space.desc}</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-auto">
                      <Link 
                        to="/signup" 
                        className="text-sm font-semibold text-primary hover:text-primary/80 flex items-center gap-1 group-hover:translate-x-1 transition-transform"
                      >
                        Try for {space.name} <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: "var(--gradient-hero)" }}
        />
        <BackgroundWaves />
        <AmbientGlow className="bottom-10 right-10 opacity-30" />
        <div className="mx-auto max-w-4xl px-6 py-16 md:py-24 text-center relative z-10 space-y-8 animate-fade-in-up">
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-tight">
            The Future of Ambient Music Is Behavioural
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground/90 max-w-2xl mx-auto leading-relaxed">
            Join the next generation of businesses using neuroscience-backed sound to create better customer experiences.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/signup"
              className="px-10 py-5 rounded-full text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/95 shadow-xl shadow-primary/25 hover:shadow-primary/45 hover:scale-105 transition-all flex items-center justify-center gap-2"
            >
              <Play className="h-5 w-5 fill-current" />
              Start Free Trial
            </Link>
          </div>
          <p className="text-md text-muted-foreground/85 font-semibold">
            Create the atmosphere your customers will remember.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                  <Music2 className="h-4 w-4 text-primary-foreground" />
                </div>
                <span className="text-xl font-semibold">Sura</span>
              </div>
              <p className="text-muted-foreground max-w-xs mb-6">
                Neuroscience-backed ambient music for businesses. Elevate customer experience with scientifically-optimized soundscapes.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">Twitter</a>
                <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">LinkedIn</a>
                <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">Instagram</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#why-exists" className="hover:text-primary transition-colors">Why Sura</a></li>
                <li><a href="#how-it-works" className="hover:text-primary transition-colors">How It Works</a></li>
                <li><a href="#why-choose" className="hover:text-primary transition-colors">Why Choose Us</a></li>
                <li><a href="#spaces" className="hover:text-primary transition-colors">Spaces</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-primary transition-colors">About</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-primary transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 rounded bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                <Music2 className="h-3 w-3 text-primary-foreground" />
              </div>
              <span className="font-semibold">Sura</span>
              <span className="text-sm text-muted-foreground ml-3">© {new Date().getFullYear()} All rights reserved</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <a href="#" className="hover:text-primary transition-colors">Terms</a>
              <a href="#" className="hover:text-primary transition-colors">Privacy</a>
              <a href="#" className="hover:text-primary transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
      <PlayerBar />
    </div>
  );
}

